'use client';

import { useEffect, useState } from 'react';
import { ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReviewLikeButtonProps {
  /** Server value; arrives as a string ("0") from the reviews payload. */
  likesCount?: number | string;
  isLiked?: boolean;
  /** Resolves when the server has settled; rejects to roll the flip back. */
  onToggle: (wasLiked: boolean) => Promise<unknown>;
  pending?: boolean;
  /** Logged-out teaser: show the count, but there is nothing to click. */
  readOnly?: boolean;
  className?: string;
}

export function ReviewLikeButton({
  likesCount = 0,
  isLiked = false,
  onToggle,
  pending = false,
  readOnly = false,
  className,
}: ReviewLikeButtonProps) {
  // Optimistic local state, same idiom as the wishlist buttons: flip on click,
  // put it back if the request fails.
  const [liked, setLiked] = useState(isLiked);
  const [count, setCount] = useState(Number(likesCount) || 0);

  // Re-sync when the list refetches (the mutation invalidates on success).
  useEffect(() => setLiked(isLiked), [isLiked]);
  useEffect(() => setCount(Number(likesCount) || 0), [likesCount]);

  const plural = count === 1 ? 'like' : 'likes';

  if (readOnly) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-sm text-[#554E49]', className)}>
        <ThumbsUp aria-hidden="true" className="h-4 w-4" />
        <span>
          {count}
          <span className="sr-only"> {plural}</span>
        </span>
      </span>
    );
  }

  const handleClick = async () => {
    if (pending) return;
    const wasLiked = liked;
    setLiked(!wasLiked);
    setCount((c) => (wasLiked ? Math.max(0, c - 1) : c + 1));
    try {
      await onToggle(wasLiked);
    } catch {
      setLiked(wasLiked);
      setCount((c) => (wasLiked ? c + 1 : Math.max(0, c - 1)));
    }
  };

  return (
    // aria-disabled rather than disabled: a disabled element gets blurred, so
    // clicking would drop keyboard focus to <body>.
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={liked}
      aria-disabled={pending}
      aria-label={`${liked ? 'Unlike' : 'Like'} this review, ${count} ${plural}`}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2 py-1 text-sm text-[#554E49] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#554E49] focus-visible:ring-offset-1',
        liked ? 'font-semibold' : 'hover:bg-[#948780]/15',
        className
      )}
    >
      <ThumbsUp aria-hidden="true" className="h-4 w-4" fill={liked ? '#554E49' : 'none'} />
      <span aria-hidden="true">{count}</span>
    </button>
  );
}

export default ReviewLikeButton;
